import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService } from '../../../../core/services'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { EditTable } from '../../../organisms'
import { Button } from 'components/atoms'
import { COLORS } from 'static/constants'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import GuideSearch from 'components/organisms/Guides/GuideSearch'

import { formatDatetimeJST } from '../../../../utils/dateFormat'
import * as constants from '../../../../static/constants'
import AgentLayoutIndex from '../_layouts'
import GuideTourSkillBulkAssignmentModal from 'components/organisms/Guides/GuideTourSkillBulkAssignmentModal'

declare var window: interfaces.IWindow

interface IProps {
  user: {
    users: IJsonResponse
    meta: interfaces.IPager
  }
}

const MAX_COUNT = 5

const CollapsibleSkillList: React.FC<{ skills: Array<{ name: string }> }> = ({ skills }) => {
  const [expanded, setExpanded] = React.useState(false)
  const showSkills = expanded ? skills : skills.slice(0, MAX_COUNT)
  return (
    <TourSkillContainer className="skills">
      {showSkills.map(skill => skill.name).join(', ')}
      {skills.length > MAX_COUNT && (
        <span
          onClick={e => {
            e.stopPropagation()
            setExpanded(v => !v)
          }}
        >
          {expanded ? I18n.t('generic.close') : I18n.t('generic.show_all')}
        </span>
      )}
    </TourSkillContainer>
  )
}

const COLUMNS = [
  {
    name: I18n.t('generic.id'),
    field: 'id',
    required: false,
    readOnly: true,
  },
  {
    name: I18n.t('generic.fullname'),
    field: 'fullname',
    renderElement: record => (
      <>
        <p>
          <a href={`/agent/guides/${record.id}/profile`} style={{ color: COLORS.primary }}>
            {record.fullname || '本名未設定'}
          </a>
        </p>
        <p>({record.username})</p>
      </>
    ),
    required: false,
  },
  {
    name: I18n.t('generic.email'),
    field: 'email',
    required: false,
  },
  {
    name: I18n.t('generic.skill'),
    renderElement: record => <CollapsibleSkillList skills={record.assigned_skills} />,
    field: 'assigned_skills',
    required: false,
  },
  {
    name: I18n.t('generic.tour_skill'),
    renderElement: record => <CollapsibleSkillList skills={record.assign_guide_tour_skills} />,
    field: 'assign_guide_tour_skills',
    required: false,
  },
  {
    name: I18n.t('generic.rank'),
    renderElement: record => record.agent_rank_as_guide?.name_i18n,
    field: 'agent_rank.name_i18n',
    required: false,
  },
  {
    name: I18n.t('generic.last_sign_in_at'),
    renderElement: record => formatDatetimeJST(record.last_app_sign_in_at),
    field: 'last_app_sign_in_at',
    required: false,
  },
  {
    name: I18n.t('user.status', { scope: 'activerecord.attributes' }),
    field: 'status_i18n',
    className: 'status',
    required: false,
  },
  {
    name: I18n.t('generic.operation'),
    field: 'operation',
    renderElement: record => (
      <div className="operations">
        <Button small={true}>
          <a href={`/agent/guides/${record.id}/edit`}>{I18n.t('generic.edit')}</a>
        </Button>
        <Button small={true}>
          <a
            href={`/agent/guide_assignment_statuses/${record.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {I18n.t('generic.assign_status')}
          </a>
        </Button>
      </div>
    ),
  },
]

const AgentGuideIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle('Agent guide')
  const [guides, setGuide] = React.useState([])
  const { data: user } = agentService.getDataFromJson(props.user)
  const [filterOptions, setFilterOptions] = React.useState(constants.DEFAULT_GUIDE_FILTER_OPTIONS)
  const [pagination, setPagination] = React.useState<interfaces.IPager>()
  const [isLoading, setIsLoading] = React.useState(true)
  const [page, setPage] = React.useState(1)
  const allGuideCount = React.useRef<number | null>(null)

  const isFiltered = React.useMemo(
    () => JSON.stringify(filterOptions) !== JSON.stringify(constants.DEFAULT_GUIDE_FILTER_OPTIONS),
    [filterOptions]
  )

  const searchGuides = React.useCallback(
    async _page => {
      try {
        setIsLoading(true)
        window.scrollTo({ top: 0 })
        const { guides: fetchedGuides, pagination: meta } = await agentService.getGuides(
          filterOptions,
          '',
          _page,
          false // with_image
        )
        setGuide(fetchedGuides)
        setPagination(meta)
        if (!isFiltered) {
          allGuideCount.current = meta.total_count
        }
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    },
    [agentService.getGuides, filterOptions, isFiltered]
  )

  const containerStyle = React.useMemo(() => {
    return `
      @container filter-container (width < 640px) {
        .filterContainer {
          width: 100%;
          max-width: 240px;
        }
      }
    `
  }, [])

  // 初回検索
  React.useEffect(() => {
    searchGuides(1)
  }, [])

  const showBulkAssignmentModal = React.useCallback(() => {
    window.globalModal.showModal({
      small: true,
      topAligned: true,
      width: '640px',
      body: (
        <GuideTourSkillBulkAssignmentModal
          onSuccess={() => searchGuides(pagination?.current_page || 1)}
        />
      ),
    })
  }, [searchGuides, pagination])

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <style type="text/css">{containerStyle}</style>
        <AgentLayoutIndex
          hideAgentNav={true}
          title={
            <Heading>
              <div className="headingInner">
                <div className="titleGroup">
                  <span>{I18n.t('generic.guides')}</span>
                  {pagination && (
                    <span className="guideCount">
                      {pagination.total_count}
                      {isFiltered && allGuideCount.current !== null && (
                        <span className="allGuideCount"> / {allGuideCount.current}</span>
                      )}
                    </span>
                  )}
                </div>

                <div className="headingActions">
                  <div className="search">
                    <GuideSearch
                      searchGuides={searchGuides}
                      filterOptions={filterOptions}
                      setFilterOptions={setFilterOptions}
                      page={page}
                      setPage={setPage}
                    />
                  </div>
                  <Button small={true} handleClick={showBulkAssignmentModal}>
                    ツアースキル一括付与
                  </Button>
                  <Button small={true} primary={true}>
                    <a href="/agent/guides/new">ガイド作成</a>
                  </Button>
                </div>
              </div>
            </Heading>
          }
          model={'guides'}
          main={
            <EditTableWrapper>
              <EditTable
                editable={false}
                columns={COLUMNS}
                records={guides}
                pagination={pagination}
                getEditLink={({ id }) => `/agent/guides/${id}/edit`}
                loading={isLoading}
                getResourceByPage={searchGuides}
                isCustomResource={true}
              />
            </EditTableWrapper>
          }
        />
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  .Index_Main {
    padding: 0 !important;

    .Panel {
      padding: 20px 40px !important;
      overflow: visible;
    }

    .Table_Data {
      padding: 0 8px 0 10px !important;
    }

    .Table_Header {
      white-space: nowrap;
      padding: 12px 8px 12px 10px !important;
    }

    .Table_Header,
    .Table_Data {
      max-width: 500px !important;
      min-width: 50px !important;
    }

    .Table_Data.hasElement .skills {
      min-width: 150px !important;
    }

    .Table_Header.status,
    .Table_Data.status {
      min-width: 80px !important;
      white-space: nowrap;
    }
  }

  .operations {
    display: flex;
    align-items: center;
    gap: 8px;

    a {
      white-space: nowrap;
    }
  }
`

const Heading = styled.div`
  .headingInner {
    display: flex;
    container-type: inline-size;
    container-name: filter-container;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px 16px;
  }
  .titleGroup {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  .guideCount {
    color: ${constants.COLORS.subText};
    font-size: 16px;
    font-weight: 600;
    &::before {
      content: '(';
      margin-right: 2px;
    }
    &::after {
      content: ')';
      margin-left: 2px;
    }
  }
  .allGuideCount {
    font-size: 11px;
  }
  .headingActions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 16px;
    button + button {
      margin-left: 0;
    }
  }
  .search {
    .filterContainer {
      margin: 0;
      color: ${constants.COLORS.subText};
      font-size: 12px;
      font-weight: normal;
      width: 400px;
    }
  }
`

const TourSkillContainer = styled.div`
  line-height: 20px;
  span {
    color: ${constants.COLORS.primary};
    text-decoration: underline;
    cursor: pointer;
    margin-left: 4px;
    white-space: nowrap;
    &:hover {
      text-decoration: none;
    }
  }
`

const EditTableWrapper = styled.div`
  .Table_Row .Table_Header:first-child,
  .Table_Row .Table_Data:first-child {
    min-width: 40px;
  }
  .Table_Scroller {
    overflow: visible;
  }
`

export default AgentGuideIndex
